import React, { useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { useWorkstreamTypes } from './hooks/useWorkstreamTypes';
import { useWorkspaceContext } from './context/WorkspaceContext';
import { Workstream } from './types';
import { getWorkstreamPosition, TimelineConfig } from './utils/timelineUtils';
import { parseDate } from './utils';

interface ParentWorkstreamBarProps {
  workstream: Workstream;
  childWorkstreams: Workstream[];
  timelineConfig: TimelineConfig;
}

// Utility to debug date positions
const debugDatePosition = (name: string, startDate: string | Date, endDate: string | Date, config: TimelineConfig) => {
  const position = getWorkstreamPosition(startDate, endDate, config);
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  
  console.log('Date Position Debug:', {
    name,
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
    position,
    dateRange: `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`
  });
  
  return position;
};

const ParentWorkstreamBar: React.FC<ParentWorkstreamBarProps> = ({
  workstream,
  childWorkstreams,
  timelineConfig,
}) => {
  const { workspaceId } = useWorkspaceContext();
  const { workstreamTypes } = useWorkstreamTypes(workspaceId);

  // Calculate positions based on dates directly with debug output
  const getChildBarPosition = (child: Workstream) => {
    return debugDatePosition(child.name, child.startDate, child.endDate, timelineConfig);
  };

  // Get parent position for no children case
  const parentPosition = debugDatePosition(
    workstream.name,
    workstream.startDate,
    workstream.endDate,
    timelineConfig
  );

  // Get all child positions (when there are children)
  const childPositions = childWorkstreams.length > 0 
    ? childWorkstreams.map(child => ({
        child,
        position: getChildBarPosition(child)
      }))
    : [];

  // Find leftmost and rightmost positions (with fallbacks for no children)
  const leftmostChild = childPositions.length > 0
    ? childPositions.reduce(
        (min, item) => item.position.left < min.position.left ? item : min,
        childPositions[0]
      )
    : { child: workstream, position: parentPosition };

  const rightmostEnd = childPositions.length > 0
    ? childPositions.reduce(
        (max, item) => {
          const right = item.position.left + item.position.width;
          const maxRight = max.position.left + max.position.width;
          return right > maxRight ? item : max;
        },
        childPositions[0]
      )
    : { child: workstream, position: parentPosition };

  // Calculate parent position manually
  const parentLeft = childPositions.length > 0 ? leftmostChild.position.left : parentPosition.left;
  const parentRight = childPositions.length > 0 
    ? rightmostEnd.position.left + rightmostEnd.position.width 
    : parentPosition.left + parentPosition.width;
  const parentWidth = parentRight - parentLeft;

  // Debug logging - now called unconditionally at the top level
  useEffect(() => {
    if (childWorkstreams.length > 0) {
      console.log('--- Parent Workstream Bar Debug ---');
      console.log('Parent:', workstream.name);
      console.log('Children count:', childWorkstreams.length);
      console.log('Leftmost child:', leftmostChild.child.name, leftmostChild.position);
      console.log('Rightmost child end:', rightmostEnd.child.name, {
        right: rightmostEnd.position.left + rightmostEnd.position.width
      });
      console.log('All child positions:', childPositions);
      console.log('Parent position:', { left: parentLeft, width: parentWidth });
      
      // Debug the actual timeline config
      console.log('Timeline Config:', {
        view: timelineConfig.view,
        zoomLevel: timelineConfig.zoomLevel,
        startDate: format(timelineConfig.startDate, 'yyyy-MM-dd'),
        endDate: format(timelineConfig.endDate, 'yyyy-MM-dd')
      });
    }
  }, [
    workstream.name, 
    childWorkstreams.length, 
    leftmostChild, 
    rightmostEnd, 
    childPositions, 
    parentLeft, 
    parentWidth,
    timelineConfig
  ]);

  // If no children, return simple parent bar
  if (childWorkstreams.length === 0) {
    return (
      <Tooltip title={`${workstream.name} (No child workstreams)`}>
        <Box
          sx={{
            position: 'absolute',
            height: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            borderRadius: 1,
            left: `${parentPosition.left}px`,
            width: `${parentPosition.width}px`,
            backgroundColor: 'rgba(200, 200, 200, 0.2)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      </Tooltip>
    );
  }

  // Calculate segments with accurate percentage positions
  const segments = childPositions.map(({ child, position }) => {
    const workstreamType = workstreamTypes.find(t => t.name === child.type);
    
    // Calculate relative positions as percentages of parent width
    const relativeLeft = position.left - parentLeft;
    const percentLeft = (relativeLeft / parentWidth) * 100;
    const percentWidth = (position.width / parentWidth) * 100;
    
    return {
      id: child.id,
      name: child.name,
      left: percentLeft,
      width: percentWidth,
      color: workstreamType?.color || '#666',
      dates: `${format(parseDate(child.startDate), 'MMM d')} - ${format(parseDate(child.endDate), 'MMM d')}`
    };
  });

  return (
    <Tooltip title={`${workstream.name} (${childWorkstreams.length} items)`}>
      <Box
        sx={{
          position: 'absolute',
          height: 24,
          top: '50%',
          transform: 'translateY(-50%)',
          borderRadius: 1,
          left: `${parentLeft}px`,
          width: `${parentWidth}px`,
          display: 'flex',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          '&:hover': {
            '& > *': {
              opacity: 0.9,
            }
          }
        }}
      >
        {segments.map((segment) => (
          <Tooltip key={segment.id} title={`${segment.name}: ${segment.dates}`}>
            <Box
              sx={{
                position: 'absolute',
                height: '100%',
                left: `${Math.max(0, segment.left)}%`,
                width: `${segment.width}%`,
                backgroundColor: segment.color,
                opacity: 0.7,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  opacity: 1,
                }
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Tooltip>
  );
};

export default ParentWorkstreamBar;